import React, { useEffect, useState } from "react";
import { NavLink, Routes, Route, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Calendar,
  Package,
  Image as ImageIcon,
  MessageSquare,
  LogOut,
  Trash2,
  Plus,
  Wine,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", label: "Bookings", icon: Calendar },
  { to: "/admin/packages", label: "Packages", icon: Package },
  { to: "/admin/media", label: "Media", icon: ImageIcon },
  { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
];

function Sidebar() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <aside className="hidden md:flex flex-col w-64 border-r border-white/8 bg-card/40 p-6 sticky top-0 h-screen" data-testid="admin-sidebar">
      <div className="flex items-center gap-3 mb-10">
        <span className="w-9 h-9 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-primary">
          <Wine size={18} strokeWidth={1.5} />
        </span>
        <div>
          <div className="font-display text-lg">Pinkies Up</div>
          <div className="overline text-primary/80 text-[9px]">Admin</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`admin-nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-white/5"
              }`
            }
          >
            <n.icon size={16} strokeWidth={1.5} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <button
        onClick={() => { logout(); navigate("/admin/login"); }}
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-destructive-foreground hover:bg-destructive/10 transition-colors"
        data-testid="admin-logout"
      >
        <LogOut size={16} strokeWidth={1.5} />
        Sign out
      </button>
    </aside>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="overline text-muted-foreground">{label}</div>
      <div className="mt-3 font-display text-4xl">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-dashboard">
      <div className="overline text-primary/80">Overview</div>
      <h1 className="mt-3 font-display text-4xl">Welcome back.</h1>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Bookings" value={stats?.total_bookings ?? "—"} />
        <StatCard label="Pending" value={stats?.pending_bookings ?? "—"} hint="Awaiting response" />
        <StatCard label="Confirmed" value={stats?.confirmed_bookings ?? "—"} />
        <StatCard label="Packages" value={stats?.packages ?? "—"} />
        <StatCard label="Media items" value={stats?.media ?? "—"} />
        <StatCard label="Inquiries" value={stats?.inquiries ?? "—"} />
      </div>
    </div>
  );
}

function Bookings() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/bookings").then((r) => setList(r.data)).catch(() => {});
  useEffect(load, []);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/bookings/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/bookings/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-bookings">
      <h1 className="font-display text-4xl mb-8">Bookings</h1>
      <div className="rounded-2xl border border-white/8 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left overline text-muted-foreground">
              <th className="px-5 py-4">Client</th>
              <th className="px-5 py-4">Event</th>
              <th className="px-5 py-4">Date</th>
              <th className="px-5 py-4">Package</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No bookings yet.</td></tr>
            )}
            {list.map((b) => (
              <tr key={b.id} className="border-t border-white/5" data-testid={`booking-row-${b.id}`}>
                <td className="px-5 py-4">
                  <div className="font-medium">{b.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{b.email} · {b.phone}</div>
                </td>
                <td className="px-5 py-4">
                  <div>{b.event_type}</div>
                  <div className="text-xs text-muted-foreground">{b.guest_count} guests · {b.location}</div>
                </td>
                <td className="px-5 py-4">{b.event_date} {b.event_time && <span className="text-xs text-muted-foreground">· {b.event_time}</span>}</td>
                <td className="px-5 py-4">{b.package_name || "—"}</td>
                <td className="px-5 py-4">
                  <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                    <SelectTrigger className="w-36 bg-secondary/40 border-white/10 h-9" data-testid={`booking-status-${b.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-4 text-right">
                  <button onClick={() => del(b.id)} className="text-muted-foreground hover:text-destructive-foreground" data-testid={`booking-delete-${b.id}`}>
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY_PKG = {
  name: "", tagline: "", description: "", price: 0, duration_hours: 4,
  guest_count: "up to 50", features: "", image_url: "", is_featured: false, order: 0,
};

function Packages() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PKG);

  const load = () => api.get("/packages").then((r) => setList(r.data)).catch(() => {});
  useEffect(load, []);

  const startNew = () => { setEditing(null); setForm(EMPTY_PKG); setOpen(true); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({ ...p, features: (p.features || []).join("\n") });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      price: Number(form.price),
      duration_hours: Number(form.duration_hours),
      order: Number(form.order),
      features: (typeof form.features === "string" ? form.features.split("\n") : form.features)
        .map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/admin/packages/${editing.id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/packages/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-packages">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl">Packages</h1>
        <Button onClick={startNew} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-package-btn">
          <Plus size={14} className="mr-2" /> New package
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((p) => (
          <div key={p.id} className="rounded-2xl border border-white/8 overflow-hidden bg-card/40" data-testid={`admin-pkg-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt="" className="aspect-[4/3] w-full object-cover" />}
            <div className="p-5">
              <div className="font-display text-xl">{p.name}</div>
              <div className="overline text-muted-foreground mt-1">£{p.price} · {p.duration_hours}h</div>
              <div className="flex gap-2 mt-5">
                <button onClick={() => startEdit(p)} className="text-xs px-3 py-1.5 rounded-full border border-white/15" data-testid={`edit-pkg-${p.id}`}>Edit</button>
                <button onClick={() => del(p.id)} className="text-xs px-3 py-1.5 rounded-full border border-destructive/30 text-destructive-foreground" data-testid={`delete-pkg-${p.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-card border-white/10" data-testid="pkg-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit package" : "New package"}</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label className="overline">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <div><Label className="overline">Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <div><Label className="overline">Price (£)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <div><Label className="overline">Duration (hrs)</Label><Input type="number" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <div><Label className="overline">Guest count</Label><Input value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <div><Label className="overline">Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <div className="md:col-span-2"><Label className="overline">Image URL</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <div className="md:col-span-2"><Label className="overline">Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <div className="md:col-span-2"><Label className="overline">Features (one per line)</Label><Textarea rows={5} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            <label className="md:col-span-2 flex items-center gap-3 text-sm">
              <input type="checkbox" checked={!!form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured / Most booked
            </label>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="pkg-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Media() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", media_type: "image", caption: "", order: 0 });

  const load = () => api.get("/media").then((r) => setList(r.data)).catch(() => {});
  useEffect(load, []);

  const save = async () => {
    try {
      await api.post("/admin/media", { ...form, order: Number(form.order) });
      toast.success("Media added");
      setOpen(false); setForm({ title: "", url: "", media_type: "image", caption: "", order: 0 }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/media/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl">Media</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-media-btn">
              <Plus size={14} className="mr-2" /> Add media
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-card border-white/10">
            <DialogHeader><DialogTitle className="font-display text-2xl">Add media</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="overline">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" data-testid="media-title" /></div>
              <div><Label className="overline">Image / Video URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" data-testid="media-url" /></div>
              <div><Label className="overline">Type</Label>
                <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v })}>
                  <SelectTrigger className="mt-2 bg-secondary/40 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="image">Image</SelectItem><SelectItem value="video">Video</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="overline">Caption</Label><Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
              <div><Label className="overline">Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-2 bg-secondary/40 border-white/10" /></div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="media-save">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {list.map((m) => (
          <div key={m.id} className="relative group rounded-xl overflow-hidden border border-white/8 aspect-square" data-testid={`admin-media-${m.id}`}>
            <img src={m.url} alt={m.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3 text-center">
              <div className="text-sm font-medium">{m.title}</div>
              <button onClick={() => del(m.id)} className="mt-2 text-xs px-3 py-1.5 rounded-full border border-destructive/30 text-destructive-foreground" data-testid={`delete-media-${m.id}`}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Inquiries() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/inquiries").then((r) => setList(r.data)).catch(() => {});
  useEffect(load, []);
  const del = async (id) => {
    try { await api.delete(`/admin/inquiries/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="admin-inquiries">
      <h1 className="font-display text-4xl mb-8">Inquiries</h1>
      <div className="space-y-4">
        {list.length === 0 && <div className="text-muted-foreground">No inquiries yet.</div>}
        {list.map((i) => (
          <div key={i.id} className="rounded-2xl border border-white/8 bg-card/40 p-6" data-testid={`inquiry-${i.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">{i.name}</div>
                <div className="text-xs text-muted-foreground">{i.email}{i.phone && ` · ${i.phone}`}</div>
              </div>
              <button onClick={() => del(i.id)} className="text-muted-foreground hover:text-destructive-foreground"><Trash2 size={15} /></button>
            </div>
            <p className="mt-4 leading-relaxed">{i.message}</p>
            <div className="mt-3 text-xs text-muted-foreground">{new Date(i.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <div className="min-h-screen flex" data-testid="admin-shell">
      <Sidebar />
      <main className="flex-1 p-8 md:p-12">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="bookings" element={<Bookings />} />
          <Route path="packages" element={<Packages />} />
          <Route path="media" element={<Media />} />
          <Route path="inquiries" element={<Inquiries />} />
        </Routes>
      </main>
    </div>
  );
}
